import { makeAutoObservable } from 'mobx';
import defaultsDeep from 'lodash/fp/defaultsDeep';

import { FilterStore } from '../../shared/util-stores';
import {
  ScanIqCustomerListFilterFormValues,
  ScanIqCustomerListFilters,
  ScanIqCustomerListSortFields,
} from '../interfaces/ScanIqCustomerListFilters';
import { resetAllStore } from '../../shared/stores/resetAllStore';
import { ScanIqCustomerListInfo } from '../interfaces/ScanIqCustomerListInfo';
import { SETUP_STATUS, SetupStatusTab } from '../interfaces/ScanIqCustomerSetupInfo';
import { scanIqCustomerService } from '../services/scanIqCustomerService';
import { SpringFiltration } from '../../shared/interfaces/SpringFiltration';

const defaultFilterData: ScanIqCustomerListFilterFormValues = {
  generalFilter: {
    entityName: '',
    superSearch: '',
  },
};

class ScanIqCustomersListStore {
  private readonly setupStatus: SetupStatusTab;
  customerList: ScanIqCustomerListInfo[] = [];
  filterMethods: FilterStore<
    ScanIqCustomerListFilterFormValues,
    ScanIqCustomerListSortFields
  >;

  constructor(setupStatus: SetupStatusTab) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.setupStatus = setupStatus;
    this.filterMethods = new FilterStore<
      ScanIqCustomerListFilterFormValues,
      ScanIqCustomerListSortFields
    >(this.handleGetCustomerList, { defaultFilterData });
  }

  setDefaultValues() {
    this.customerList = [];
    this.filterMethods.setDefaultValues();
  }

  private async handleGetCustomerList(
    filterInfo: SpringFiltration<
      ScanIqCustomerListFilterFormValues,
      ScanIqCustomerListSortFields
    >,
  ) {
    // defaultsDeep from lodash/fp does not mutate and lets filterInfo override defaults
    const requestInfo: ScanIqCustomerListFilters = defaultsDeep(
      { filters: { generalFilter: { scanIqCustomerSetupStatus: this.setupStatus } } },
      filterInfo,
    );

    const data = await scanIqCustomerService.getCustomerList(requestInfo);

    this.setCustomerList(data.content);

    return data;
  }

  async getCustomerList() {
    return this.filterMethods.getData();
  }

  setCustomerList(customerList: ScanIqCustomerListInfo[]) {
    this.customerList = customerList;
  }
}

// instance per tab so filters/pagination/sort survive tab switching
const completedScanIqCustomerListStore = new ScanIqCustomersListStore(
  SETUP_STATUS.COMPLETED,
);
const needSetupScanIqCustomerListStore = new ScanIqCustomersListStore(
  SETUP_STATUS.NEED_SETUP,
);

resetAllStore.addResetMethod(completedScanIqCustomerListStore.setDefaultValues);
resetAllStore.addResetMethod(needSetupScanIqCustomerListStore.setDefaultValues);

export {
  completedScanIqCustomerListStore,
  needSetupScanIqCustomerListStore,
  ScanIqCustomersListStore,
};
